import { StateCreator } from 'zustand'
import { userApi } from '../../lib/api'
import { useAuthStore } from '../authStore'
import { DEFAULT_SETTINGS } from '../../constants/invoice'
import { SettingsManager } from '../../utils/settingsManager'
import type { InvoiceStore } from '../invoiceStore'
import type { Settings } from '@dutchinvoice/types'

export interface SettingsSlice {
    settings: Settings
    updateSettings: (settings: Partial<Settings>) => void
    resetSettings: () => void
}

export const createSettingsSlice: StateCreator<
    InvoiceStore,
    [],
    [],
    SettingsSlice
> = (set, get) => ({
    settings: DEFAULT_SETTINGS,

    updateSettings: (newSettings: Partial<Settings>) => {
        // First update local state immediately
        set((state) => {
            // Use SettingsManager to merge settings
            const updatedSettings = SettingsManager.mergeSettings(state.settings, newSettings);

            // If there's a default company and current invoice is concept, apply it
            if (updatedSettings.defaultCompany && state.currentInvoice.status === 'concept') {
                return {
                    settings: updatedSettings,
                    currentInvoice: {
                        ...state.currentInvoice,
                        sender: {
                            ...updatedSettings.defaultCompany,
                            isPlaceholder: undefined
                        }
                    }
                }
            }

            // Removed language change effects - now handled by the LanguageSwitcher component

            return { settings: updatedSettings }
        })

        // Only sync with API if user is logged in
        if (useAuthStore.getState().isAuthenticated) {
            // Get the complete current settings after the state update
            const currentSettings = get().settings

            // Send complete settings object to backend
            userApi.updateSettings(currentSettings).catch((error) => {
                set({
                    error: {
                        code: 'SETTINGS_UPDATE_ERROR',
                        message: 'Failed to sync settings with server',
                        details: error
                    }
                })
            })
        }
    },

    resetSettings: () => {
        set(() => ({
            settings: DEFAULT_SETTINGS
        }))
        localStorage.removeItem('dutchinvoice_local_settings')
    },
}) 